// src/Components/ManageTransactions/ManageTransactions.js
import React, { useEffect, useMemo, useState } from "react";
import {
	Alert, Backdrop, Box, Button, Card, CardContent, CardHeader, Chip, CircularProgress, Container, Dialog,
	DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton, InputAdornment, MenuItem, Paper,
	Select, Snackbar, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
	Tooltip, Typography, useMediaQuery,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useNavigate } from "react-router-dom";
import {
	Refresh as RefreshIcon,
	Search as SearchIcon,
	FilterListRounded as FilterIcon,
	AttachMoney as DollarIcon,
	TrendingUp as TrendingUpIcon,
	CalendarToday as CalendarIcon,
	CreditCard as CreditCardIcon,
	Visibility as VisibilityIcon,
	ContentCopy as CopyIcon,
	Launch as ExternalLinkIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import OrderDetails from "../ManageOrders/OrderDetails";
import { ThemeProvider } from "@mui/material/styles";
import { buttonTheme } from "../../theme";
import { config } from "../../config";
import { getVendorId } from "../../utils/authSession";

/* ───────── helpers ───────── */
const INR = new Intl.NumberFormat("en-IN", {
	style: "currency",
	currency: "INR",
	minimumFractionDigits: 2,
});

const STATUS_META = {
	UPI_COMPLETED: { label: "Success", color: "success" },
	UPI_PENDING: { label: "Pending", color: "warning" },
	UPI_FAILED: { label: "Failed", color: "error" },
};

/* ───────── component ───────── */
export default function ManageTransactions() {

	const navigate = useNavigate();

	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [refreshing, setRefreshing] = useState(false);
	const [orderLoading, setOrderLoading] = useState(false);

	const sessionToken = () =>
		sessionStorage.getItem("sessionToken") || localStorage.getItem("sessionToken");


	/* filters */
	const [search, setSearch] = useState("");
	const [statusFilter, setStatusFilter] = useState("all");

	/* order dialog */
	const [orderDlgOpen, setOrderDlgOpen] = useState(false);
	const [selectedOrder, setSelectedOrder] = useState(null);

	/* payment dialog */
	const [paymentDlgOpen, setPaymentDlgOpen] = useState(false);
	const [paymentDetails, setPaymentDetails] = useState(null);
	const [paymentLoading, setPaymentLoading] = useState(false);
	const [paymentError, setPaymentError] = useState(null);

	/* copy snackbar */
	const [copyMsg, setCopyMsg] = useState("");

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md")); // ≤ 960 px → card list

	/* fetch all payments */
	const fetchPayments = async () => {
		const vendorId = getVendorId();
		if (!vendorId) {
			setError("Vendor ID missing in localStorage.");
			setLoading(false);
			return;
		}
		try {
			setLoading(true);
			const r = await fetch(
				`${config.API_MSWSSTOREMANAGER_BASE_URL}/v1/vendors/${vendorId}/payments`,
				{ headers: { Accept: "application/json", "X-Session-Token": sessionToken() } },
			);
			if (!r.ok) throw new Error(`HTTP ${r.status}`);
			setRows(await r.json());
		} catch (e) {
			setError(e.message ?? "Failed to load transactions");
		} finally {
			setLoading(false);
			setRefreshing(false);
		}
	};

	useEffect(() => {
		fetchPayments();
	}, []);

	/* filtered rows */
	const filteredRows = useMemo(() => {
		let out = rows;
		if (statusFilter !== "all") out = out.filter((r) => r.status === statusFilter);
		if (search.trim()) {
			const q = search.toLowerCase();
			out = out.filter(
				(r) =>
					String(r.orderId).toLowerCase().includes(q) ||
					String(r.paymentId).toLowerCase().includes(q),
			);
		}
		return out;
	}, [rows, search, statusFilter]);

	/* stats */
	const totalAmount = rows.reduce((sum, r) => sum + r.amountPaid, 0);
	const successCount = rows.filter((r) => r.status === "UPI_COMPLETED").length;
	const successRate = rows.length ? (successCount / rows.length) * 100 : 0;

	/* helpers */
	const copyToClipboard = (text, label) => {
		navigator.clipboard.writeText(text);
		setCopyMsg(`${label} copied to clipboard`);
	};

	const openOrderDialog = (row) => {
		setOrderLoading(true); // 👈 show backdrop

		setSelectedOrder({
			id: row.orderId,
			orderNumber: row.orderId,
			amountPaid: row.amountPaid,
			createdAt: row.createdAt,
		});

		// Small delay to show loader, then open dialog
		setTimeout(() => {
			setOrderDlgOpen(true);
			setOrderLoading(false); // 👈 hide backdrop
		}, 300);
	};

	const openPaymentDialog = async (row) => {
		const vendorId = getVendorId();
		if (!vendorId) return;
		setLoading(true);
		setPaymentDlgOpen(true);
		setPaymentLoading(true);
		setPaymentError(null);
		try {
			const url = `${config.API_MSWSSTOREMANAGER_BASE_URL}/v1/vendors/${vendorId}/payments/${row.paymentId}`;
			const r = await fetch(url, { headers: { Accept: "application/json", "X-Session-Token": sessionToken() } });
			if (!r.ok) throw new Error(`HTTP ${r.status}`);
			setPaymentDetails(await r.json());
		} catch (e) {
			setPaymentError(e.message ?? "Failed to load payment details");
		} finally {
			setLoading(false);
			setPaymentLoading(false);
		}
	};

	/* ───────── UI ───────── */
	return (
		<>
			<ThemeProvider theme={theme}>
				<Container
					maxWidth="lg"
					sx={{
						pt: { xs: 1.5, sm: 4, md: 6 },     // was py: 6
						pb: { xs: 2, sm: 6 },
						display: "flex",
						flexDirection: "column",
						gap: { xs: 2, sm: 4, md: 6 },      // less section spacing on xs
					}}
				>
					{/* header */}
					<Stack
						direction={{ xs: "row", sm: "row" }}
						alignItems="center"
						justifyContent="space-between"
						gap={{ xs: 1.25, sm: 2 }}
						sx={{ mb: { xs: 1.25, sm: 2 } }}
					>
						{/* Left: Back + Title */}
						<Stack direction="row" alignItems="center" spacing={1.25}>
							{isMobile ? (
								<IconButton
									onClick={() => navigate("/")}
									aria-label="Go back"
									size="small"
									sx={{
										width: 36, height: 36, borderRadius: 999,
										border: (t) => `1px solid ${t.palette.divider}`,
										bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
										"&:hover": { bgcolor: (t) => t.palette.action.hover },
									}}
								>
									<ArrowBackIosNewIcon fontSize="small" />
								</IconButton>
							) : (
								<Button
									onClick={() => navigate("/")}
									startIcon={<ArrowBackIosNewIcon />}
									variant="outlined"
									size="small" color="secondary"
									sx={{
										borderRadius: 999,
										textTransform: "none",
										px: 1.25, py: 0.75, fontWeight: 600,
										borderColor: "divider",
										bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
										"&:hover": { bgcolor: (t) => t.palette.action.hover },
									}}
								>
									Back
								</Button>
							)}

							<Box>
								<Typography
									variant="h4"
									sx={{
										fontWeight: 800,
										fontSize: { xs: 20, sm: 28 },   // smaller on mobile
										lineHeight: 1.15,
										letterSpacing: "-0.2px",
									}}
								>
									Transactions
								</Typography>

								{/* hide verbose subheader on mobile, show compact caption if you still want context */}
								<Typography
									color="text.secondary"
									sx={{ display: { xs: "none", sm: "block" } }}
								>
									Manage and monitor your payment transactions
								</Typography>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: { xs: "block", sm: "none" }, mt: 0.25 }}
								>
									Manage payments
								</Typography>
							</Box>
						</Stack>

						{/* Right: smaller refresh on mobile */}
						<Button
							variant="outlined" color="secondary"
							size={isMobile ? "small" : "medium"}
							startIcon={
								<RefreshIcon
									sx={{
										animation: refreshing ? "spin 1s linear infinite" : "none",
										"@keyframes spin": { to: { transform: "rotate(360deg)" } },
									}}
								/>
							}
							onClick={() => {
								setRefreshing(true);
								fetchPayments();
							}}
							sx={{ px: { xs: 1.25, sm: 2 }, py: { xs: 0.5, sm: 1 } }}
						>
							Refresh
						</Button>
					</Stack>


					{/* stats */}
					{/* stats (flex instead of Grid) */}
					<Box
						sx={{
							display: "flex",
							flexWrap: "wrap",
							gap: 2,                        // spacing between cards
						}}
					>
						{[
							{
								title: "Total Revenue",
								value: INR.format(totalAmount),
								note: `From ${rows.length} transactions`,
								icon: <DollarIcon color="disabled" sx={{ fontSize: { xs: 18, sm: 20 } }} />,
							},
							{
								title: "Success Rate",
								value: `${successRate.toFixed(1)}%`,
								note: `${successCount} successful payments`,
								icon: <TrendingUpIcon color="disabled" sx={{ fontSize: { xs: 18, sm: 20 } }} />,
							},
							{
								title: "This Month",
								value: rows.length,
								note: "Total transactions",
								icon: <CalendarIcon color="disabled" sx={{ fontSize: { xs: 18, sm: 20 } }} />,
							},
						].map((c) => (
							<Box
								key={c.title}
								sx={{
									width: { xs: "100%", sm: "calc(50% - 8px)", md: "calc(33.333% - 12px)" }, // 1/2/3 columns
									flexShrink: 0,
								}}
							>
								<Card sx={{ height: "100%" }}>
									<CardHeader
										title={
											<Typography
												variant="subtitle2"
												color="text.secondary"
												sx={{ fontSize: { xs: 12, sm: 13 } }}
											>
												{c.title}
											</Typography>
										}
										action={<Box sx={{ display: "flex", alignItems: "center" }}>{c.icon}</Box>}
										sx={{ pb: 0, "& .MuiCardHeader-action": { alignSelf: "center" } }}
									/>
									<CardContent sx={{ pt: 1, pb: { xs: 1.25, sm: 2 }, px: { xs: 1.25, sm: 2 } }}>
										<Typography
											variant="h5"
											fontWeight={700}
											sx={{ fontSize: { xs: 20, sm: 22, md: 24 }, lineHeight: 1.2 }}
										>
											{c.value}
										</Typography>
										<Typography
											variant="caption"
											color="text.secondary"
											sx={{ fontSize: { xs: 11, sm: 12 } }}
										>
											{c.note}
										</Typography>
									</CardContent>
								</Card>
							</Box>
						))}
					</Box>


					{/* error banner */}
					{error && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{error}
						</Alert>
					)}

					{/* filter + list */}
					<Card>
						<CardHeader
							title={
								<Stack direction="row" alignItems="center" gap={1}>
									<CreditCardIcon fontSize="small" />
									<Typography variant="subtitle1" fontWeight={600}>
										Transaction History
									</Typography>
								</Stack>
							}
							subheader="View and filter your payment transactions"
						/>
						<CardContent>
							{/* filters */}
							<Stack
								direction={{ xs: "column", sm: "row" }}
								gap={2}
								mb={4}
								alignItems={{ xs: "stretch", sm: "center" }}
							>
								<TextField
									fullWidth
									size="small"
									placeholder="Search by Order ID or Payment ID..."
									value={search}
									onChange={(e) => setSearch(e.target.value)}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<SearchIcon fontSize="small" />
											</InputAdornment>
										),
									}}
								/>
								<Box sx={{ minWidth: 170 }}>
									<Select
										fullWidth
										size="small"
										value={statusFilter}
										onChange={(e) => setStatusFilter(e.target.value)}
										startAdornment={
											<InputAdornment position="start">
												<FilterIcon fontSize="small" />
											</InputAdornment>
										}
									>
										<MenuItem value="all">All Status</MenuItem>
										<MenuItem value="UPI_COMPLETED">Success</MenuItem>
										<MenuItem value="UPI_PENDING">Pending</MenuItem>
										<MenuItem value="UPI_FAILED">Failed</MenuItem>
									</Select>
								</Box>
							</Stack>

							{/* list */}
							{filteredRows.length === 0 ? (
								<Box sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
									<CreditCardIcon sx={{ fontSize: 40, mb: 2 }} color="disabled" />
									<Typography variant="h6" mb={1}>
										No transactions found
									</Typography>
									<Typography variant="body2">
										{search || statusFilter !== "all"
											? "Try adjusting your search or filter criteria"
											: "Your transactions will appear here once you start receiving payments"}
									</Typography>
								</Box>
							) : isMobile ? (
								/* ───── Mobile / Tablet → Card list ───── */
								<Stack spacing={2}>
									{filteredRows.map((row) => {
										const date = dayjs(row.createdAt).format("DD MMM YYYY, hh:mm A");
										const meta = STATUS_META[row.status] ?? {
											label: row.status,
											color: "default",
										};
										return (
											<Card key={row.paymentId} sx={{ p: 2 }}>
												{/* status & amount */}
												<Stack direction="row" justifyContent="space-between" alignItems="center">
													<Chip label={meta.label} color={meta.color} size="small" />
													<Typography variant="subtitle1" fontWeight={700}>
														{INR.format(row.amountPaid)}
													</Typography>
												</Stack>

												{/* IDs */}
												<Grid container spacing={2} sx={{ mt: 1 }}>
													<Grid item xs={6}>
														<Typography variant="caption" color="text.secondary">
															Order ID
														</Typography>
														<Box
															component="code"
															sx={{
																display: "inline-block",
																mt: 0.3,
																px: 0.6,
																py: 0.3,
																borderRadius: 1,
																backgroundColor: theme.palette.action.hover,
																fontFamily: "monospace",
																fontSize: 13,
															}}
														>
															#{row.orderId}
														</Box>
													</Grid>
													<Grid item xs={6}>
														<Typography variant="caption" color="text.secondary">
															Payment ID
														</Typography>
														<Box
															component="code"
															sx={{
																display: "inline-block",
																mt: 0.3,
																px: 0.6,
																py: 0.3,
																borderRadius: 1,
																backgroundColor: theme.palette.action.hover,
																fontFamily: "monospace",
																fontSize: 13,
																color: theme.palette.text.secondary,
															}}
														>
															{row.paymentId}
														</Box>
													</Grid>
												</Grid>

												{/* date & method */}
												<Grid container spacing={2} sx={{ mt: 1 }}>
													<Grid item xs={6}>
														<Typography variant="caption" color="text.secondary">
															Date &amp; Time
														</Typography>
														<Typography variant="body2">{date.split(",")[0]}</Typography>
														<Typography variant="caption" color="text.secondary">
															{date.split(",")[1]}
														</Typography>
													</Grid>
													<Grid item xs={6}>
														<Typography variant="caption" color="text.secondary">
															Method
														</Typography>
														<Stack direction="row" alignItems="center" gap={1} sx={{ mt: 0.3 }}>
															<CreditCardIcon fontSize="small" color="disabled" />
															<Typography
																variant="body2"
																sx={{ textTransform: "uppercase", fontWeight: 600 }}
															>
																{row.paymentMethod}
															</Typography>
														</Stack>
													</Grid>
												</Grid>

												{/* actions */}
												<Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
													<Button
														fullWidth
														size="small"
														variant="contained" sx={{fontWeight: 600}}
														onClick={() => openPaymentDialog(row)}
													>
														View Payment
													</Button>
													<Button
														fullWidth
														size="small" 
														variant="text" sx={{fontWeight: 600, color: 'secondary.main'}}
														onClick={() => openOrderDialog(row)}
													>
														View Order
													</Button>
												</Stack>
											</Card>
										);
									})}
								</Stack>
							) : (
								/* ───── Desktop → Table ───── */
								<TableContainer
									component={Paper}
									sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}
								>
									<Table>
										<TableHead>
											<TableRow>
												<TableCell>Date &amp; Time</TableCell>
												<TableCell>Order ID</TableCell>
												<TableCell>Payment ID</TableCell>
												<TableCell align="right">Amount</TableCell>
												<TableCell>Method</TableCell>
												<TableCell>Status</TableCell>
												<TableCell align="center">Actions</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{filteredRows.map((row) => {
												const fmt = dayjs(row.createdAt).format("DD MMM YYYY, hh:mm A");
												const [datePart, timePart] = fmt.split(",");
												const meta =
													STATUS_META[row.status] ?? { label: row.status, color: "default" };
												return (
													<TableRow key={row.paymentId} hover>
														<TableCell>
															<Typography variant="body2">{datePart}</Typography>
															<Typography variant="caption" color="text.secondary">
																{timePart.trim()}
															</Typography>
														</TableCell>
														<TableCell>
															<Box
																component="code"
																sx={{
																	px: 0.6,
																	py: 0.3,
																	borderRadius: 1,
																	backgroundColor: theme.palette.action.hover,
																	fontFamily: "monospace",
																	fontSize: 13,
																	fontWeight: 500,
																}}
															>
																#{row.orderId}
															</Box>
														</TableCell>
														<TableCell>
															<Box
																component="code"
																sx={{
																	px: 0.6,
																	py: 0.3,
																	borderRadius: 1,
																	backgroundColor: theme.palette.action.hover,
																	fontFamily: "monospace",
																	fontSize: 13,
																	color: theme.palette.text.secondary,
																}}
															>
																{row.paymentId}
															</Box>
														</TableCell>
														<TableCell align="right" sx={{ fontWeight: 600 }}>
															{INR.format(row.amountPaid)}
														</TableCell>
														<TableCell>
															<Stack direction="row" alignItems="center" gap={1}>
																<CreditCardIcon fontSize="small" color="disabled" />
																<Typography
																	variant="body2"
																	sx={{ textTransform: "uppercase", fontWeight: 500 }}
																>
																	{row.paymentMethod}
																</Typography>
															</Stack>
														</TableCell>
														<TableCell>
															<Chip label={meta.label} color={meta.color} size="small" />
														</TableCell>
														<TableCell align="center">
															<Stack direction="row" spacing={1} justifyContent="center">
																<Button
																	size="small"
																	variant="contained"
																	onClick={() => openPaymentDialog(row)}
																>
																	View Payment
																</Button>
																<Tooltip title="View Order">
																	<IconButton
																		size="small"
																		onClick={() => openOrderDialog(row)}
																	>
																		<ExternalLinkIcon fontSize="small" />
																	</IconButton>
																</Tooltip>
															</Stack>
														</TableCell>
													</TableRow>
												);
											})}
										</TableBody>
									</Table>
								</TableContainer>
							)}
						</CardContent>
					</Card>
				</Container>

				{/* order dialog */}
				<OrderDetails
					open={orderDlgOpen}
					onClose={() => setOrderDlgOpen(false)}
					order={selectedOrder}
					selectedDate={selectedOrder?.createdAt ? dayjs(selectedOrder.createdAt) : null}
				/>

				{/* ─── Payment Details Dialog (no Grid) ─── */}
				<Dialog
					open={paymentDlgOpen}
					onClose={() => setPaymentDlgOpen(false)}
					maxWidth="sm"
					fullWidth fullScreen={isMobile}
					scroll="paper"
				>
					{/* header */}
					<DialogTitle
						sx={{
							px: 3,
							py: 2,
							display: "flex",
							alignItems: "center",
							gap: 1,
							borderBottom: `1px solid ${theme.palette.divider}`,
						}}
					>
						<CreditCardIcon fontSize="small" />
						<Typography variant="h6" fontWeight={600}>
							Payment Details
						</Typography>
					</DialogTitle>

					{/* body */}
					<DialogContent dividers sx={{ p: 3 }}>
						{paymentLoading ? (
							<Box sx={{ textAlign: "center", py: 8 }}>
								<CircularProgress />
							</Box>
						) : paymentError ? (
							<Alert severity="error">{paymentError}</Alert>
						) : paymentDetails ? (
							<Stack spacing={3}>
								{/* 1 ▸ status & method */}
								<Stack
									direction="row"
									flexWrap="wrap"
									spacing={2}
									useFlexGap
								>
									{[
										{
											label: "Transaction Status",
											content: (
												<Chip
													size="small"
													label={
														STATUS_META[paymentDetails.status]?.label ?? paymentDetails.status
													}
													color={STATUS_META[paymentDetails.status]?.color ?? "default"}
													sx={{ fontWeight: 600 }}
												/>
											),
										},
										{
											label: "Payment Method",
											content: (
												<Stack direction="row" alignItems="center" spacing={1}>
													<CreditCardIcon fontSize="small" color="disabled" />
													<Typography variant="subtitle2" fontWeight={600}>
														{paymentDetails.paymentMethod.toUpperCase()}
													</Typography>
												</Stack>
											),
										},
									].map((item) => (
										<Paper
											key={item.label}
											variant="outlined"
											sx={{
												width: { xs: "100%", sm: "calc(50% - 8px)" }, // two-up when ≥ 600 px
												p: 2,
												borderRadius: 2,
												bgcolor: "grey.50",
											}}
										>
											<Typography
												variant="caption"
												color="text.secondary"
												sx={{ mb: 1, display: "block" }}
											>
												{item.label}
											</Typography>
											{item.content}
										</Paper>
									))}
								</Stack>

								{/* 2 ▸ TR ANSACTION IDs – same responsive sizing */}
								<Stack
									direction="row"
									flexWrap="wrap"
									spacing={2}
									useFlexGap
								>
									{[
										["Order ID", `#${paymentDetails.orderId}`],
										["Payment ID", paymentDetails.paymentId],
									].map(([lbl, val]) => (
										<Paper
											key={lbl}
											variant="outlined"
											sx={{
												width: { xs: "100%", sm: "calc(50% - 8px)" },
												p: 2,
												borderRadius: 2,
												display: "flex",
												justifyContent: "space-between",
												alignItems: "flex-start",
												bgcolor: "grey.50",
											}}
										>
											<Box sx={{ mr: 2, minWidth: 0 }}>
												<Typography
													variant="caption"
													color="text.secondary"
													sx={{ mb: 0.5, display: "block" }}
												>
													{lbl}
												</Typography>
												<Typography variant="subtitle2" fontFamily="monospace">
													{val}
												</Typography>
											</Box>
											<Tooltip title="Copy">
												<IconButton size="small" onClick={() => copyToClipboard(val, lbl)}>
													<CopyIcon fontSize="small" />
												</IconButton>
											</Tooltip>
										</Paper>
									))}
								</Stack>

								{/* 3 ▸ amount breakdown */}
								<Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
									<Typography variant="caption" color="text.secondary">
										Amount Breakdown
									</Typography>
									<Stack spacing={1.2} sx={{ mt: 1 }}>
										{[
											["UPI Total", paymentDetails.upiTotal],
											["Amount Paid", paymentDetails.amountPaid, theme.palette.success.main],
											["Tips", paymentDetails.tips],
										].map(([l, v, clr]) => (
											<Stack direction="row" justifyContent="space-between" key={l}>
												<Typography variant="body2">{l}</Typography>
												<Typography
													variant="body2"
													fontWeight={600}
													color={clr || "text.primary"}
												>
													{INR.format(v)}
												</Typography>
											</Stack>
										))}
										<Divider />
										<Stack direction="row" justifyContent="space-between">
											<Typography variant="subtitle1" fontWeight={700}>
												Total
											</Typography>
											<Typography variant="subtitle1" fontWeight={700}>
												{INR.format(paymentDetails.amountPaid + paymentDetails.tips)}
											</Typography>
										</Stack>
									</Stack>
								</Paper>

								{/* 4 ▸ UPI details */}
								<Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
									<Typography variant="caption" color="text.secondary">
										UPI DETAILS
									</Typography>

									<Stack spacing={1.5} sx={{ mt: 1 }}>
										{[
											{ label: "Payee UPI ID", value: paymentDetails.payeeUpiId },
											{ label: "Payer UPI ID", value: paymentDetails.payerUpiId },
										].map(({ label, value }) => (
											<Paper
												key={label}
												variant="outlined"
												sx={{
													px: 1.5,
													py: 1,
													borderRadius: 1.5,
													display: "flex",
													justifyContent: "space-between",
													alignItems: "center",
													bgcolor: "grey.50",
												}}
											>
												<Box sx={{ mr: 2, minWidth: 0 }}>
													<Typography
														variant="caption"
														color="text.secondary"
														sx={{ mb: 0.5, display: "block" }}
													>
														{label}
													</Typography>
													<Typography
														variant="body2"
														fontFamily="monospace"
														sx={{ wordBreak: "break-all" }}
													>
														{value}
													</Typography>
												</Box>
												<Tooltip title="Copy">
													<IconButton size="small" onClick={() => copyToClipboard(value, label)}>
														<CopyIcon fontSize="small" />
													</IconButton>
												</Tooltip>
											</Paper>
										))}

										{/* UPI App card */}
										<Paper
											variant="outlined"
											sx={{
												px: 1.5,
												py: 1,
												borderRadius: 1.5,
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												bgcolor: "grey.50",
											}}
										>
											<Box sx={{ mr: 2 }}>
												<Typography
													variant="caption"
													color="text.secondary"
													sx={{ mb: 0.5, display: "block" }}
												>
													UPI App
												</Typography>
												<Typography variant="body2">{paymentDetails.upiApp}</Typography>
											</Box>
											<ExternalLinkIcon fontSize="small" color="disabled" />
										</Paper>
									</Stack>
								</Paper>

								{/* 5 ▸ timestamp */}
								<Stack direction="row" spacing={1} justifyContent="flex-end">
									<CalendarIcon fontSize="small" color="disabled" />
									<Typography variant="caption" color="text.secondary">
										{dayjs(paymentDetails.createdAt).format("DD MMM YYYY • HH:mm:ss")}
									</Typography>
								</Stack>
							</Stack>
						) : null}
					</DialogContent>

					{/* footer */}
					<DialogActions sx={{ px: 3, py: 1.5 }}>
						<Button color="error" sx={{fontWeight: 600}} onClick={() => setPaymentDlgOpen(false)} size="small">
							Close
						</Button>
					</DialogActions>
				</Dialog>


				{/* backdrop */}
				<Backdrop open={loading} sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
					<CircularProgress color="inherit" />
				</Backdrop>
				<Backdrop
					open={orderLoading}
					sx={{ color: "#fff", zIndex: (t) => t.zIndex.modal + 1 }}
				>
					<CircularProgress color="inherit" />
				</Backdrop>
				{/* error snackbar */}
				<Snackbar
					open={Boolean(error)}
					autoHideDuration={6000}
					onClose={() => setError(null)}
				>
					<Alert onClose={() => setError(null)} severity="error" sx={{ width: "100%" }}>
						{error}
					</Alert>
				</Snackbar>

				{/* copy snackbar */}
				<Snackbar
					open={Boolean(copyMsg)}
					autoHideDuration={3000}
					onClose={() => setCopyMsg("")}
				>
					<Alert severity="success" onClose={() => setCopyMsg("")}>
						{copyMsg}
					</Alert>
				</Snackbar>
			</ThemeProvider>
		</>
	);
}
